import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import { checkToken, fetchUserProfile, loadInitialData } from "./actions/authActions";
import withClearCache from "./ClearCache";
import NotificationsHandler from "./components/Notifications";
import { THEME } from "./constants";
import { getCurrentTheme, isUserAuthorized } from "./store/selectors";
import { applyThemeVars, buildAppTheme } from "./theme";
import { getCookie } from "./utils";

const MainContainer = lazy(() => import("./containers/MainContainer"));
const AuthContainer = lazy(() => import("./containers/AuthContainer"));

const ClearCacheComponent = withClearCache(() => null);

const App = () => {
	const dispatch = useDispatch();
	const isLight = useSelector(getCurrentTheme);
	const { auth } = useSelector(isUserAuthorized) || {};

	const fetchInitialData = useCallback(() => {
		dispatch(fetchUserProfile());
		dispatch(loadInitialData());
	}, [dispatch]);

	const checkPersistency = useCallback(() => {
		dispatch(checkToken());
	}, [dispatch]);

	useEffect(() => {
		auth ? fetchInitialData() : checkPersistency();
	}, [auth, fetchInitialData, checkPersistency]);

	// Sin preferencia guardada se sigue el modo del dispositivo.
	useEffect(() => {
		const refreshTheme = async () => {
			const stored = await getCookie(THEME);
			const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
			dispatch({ type: THEME, payload: stored ?? !prefersDark });
		};
		refreshTheme();
	}, [dispatch]);

	useLayoutEffect(() => applyThemeVars(isLight), [isLight]);

	const theme = useMemo(() => buildAppTheme(isLight), [isLight]);

	return (
		<ThemeProvider theme={theme}>
			<Suspense>
				<ClearCacheComponent />
				{auth ? <MainContainer /> : <AuthContainer />}
			</Suspense>
			<NotificationsHandler />
		</ThemeProvider>
	);
};

export default App;
